<template>
    <div id="invoiceDetail" v-if="showingInvoice">
        <AppBreadCrumb :paths="breadcrumbPaths" :select="true" class="breadcrumb" />
        <div class="invoice-detail-content">
            <div class="invoice-summary">
                <div class="summary-card">
                    <div class="summary-item">
                        <label>Period</label>
                        <div class="value description">{{ showingInvoice.period }}</div>
                    </div>
                    <div class="summary-item" v-if="showingInvoice.description">
                        <label>{{ $t("message.description") }}</label>
                        <div class="value description">{{ showingInvoice.description }}</div>
                    </div>
                </div>

                <div class="price-summary">
                    <div class="price-summary__head">
                        <div class="summary-head__status">
                            <div
                                :class="[
                                    'status',
                                    isForecast
                                        ? 'forecast'
                                        : showingInvoice.refund
                                        ? 'refund'
                                        : showingInvoice.paid
                                        ? 'paid'
                                        : 'not-paid',
                                ]"
                            >
                                {{
                                    isForecast
                                        ? "Bekleniyor"
                                        : showingInvoice.refund
                                        ? "İade Edildi"
                                        : showingInvoice.paid
                                        ? "Ödendi"
                                        : "Ödenmedi"
                                }}
                            </div>
                        </div>
                        <div class="summary-head__period">
                            <a-switch
                                v-if="isForecast"
                                v-model="showingUntilToday"
                                checked-children="Bugüne Kadar"
                                un-checked-children="Ay Sonu"
                                class="forecast-toggle"
                            />
                        </div>
                    </div>
                    <div class="price-summary__content">
                        <div class="price-item">
                            <span>Toplam</span>
                            <MoneyFormatter :price="showingInvoice.prices.price" className="total-price" />
                        </div>
                        <div class="price-item tax">
                            <span>KDV (%{{ vatRate }})</span>
                            <MoneyFormatter :price="showingInvoice.prices.taxPrice" className="total-price" />
                        </div>
                        <div class="price-item total">
                            <span>{{ $t("billing.totalPrice") }}</span>
                            <MoneyFormatter :price="showingInvoice.prices.totalPrice" className="total-price" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="package-groups" v-if="showingInvoice.plans.length > 0">
                <div class="package-group" v-for="(planGroup, index) in showingInvoice.plans" :key="index">
                    <div class="group-header">
                        <h3>{{ planGroup.title }}</h3>
                        <div class="group-total" v-if="planGroup.price > 0">
                            <MoneyFormatter :price="planGroup.price" className="total-price" />
                        </div>
                    </div>

                    <div class="package-items">
                        <div class="package-item" v-for="(item, itemIndex) in planGroup.items" :key="itemIndex">
                            <div class="item-info">
                                <div class="item-title">
                                    {{ item.title }}
                                    <span class="item-details">
                                        {{ getPackageDetails(item) }}
                                    </span>
                                </div>
                                <div class="item-period" v-if="item.startDate">
                                    {{ item.startDate }} - {{ item.endDate }}
                                </div>
                            </div>
                            <div class="item-price" v-if="item.price > 0">
                                <MoneyFormatter :price="item.price" className="total-price" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppBreadCrumb from "../../../components/common/AppBreadCrumb";
import { getDetailInvoice, CHANNEL_PLAN_TYPES } from "./utils/util";
import _ from "lodash";
import { mapActions, mapState } from "vuex";
import moment from "moment";
import MoneyFormatter from "../../../components/MoneyFormatter.vue";

const { getInvoiceById } = require("../../../api/billing");

export default {
    name: "InvoiceDetail",
    components: { MoneyFormatter, AppBreadCrumb },
    data() {
        return {
            showingUntilToday: false,
            untilToday: {
                period: "",
                paid: false,
                prices: {
                    totalPrice: 0,
                },
                plans: [],
            },
            endOfMonthForecast: {
                period: "",
                paid: false,
                prices: {
                    totalPrice: 0,
                },
                plans: [],
            },
            invoiceDetails: {
                isManuel: false,
                description: "",
                period: "",
                paid: false,
                prices: {
                    totalPrice: 0,
                },
                plans: [],
                vatRate: null,
                type: null,
            },
        };
    },
    methods: {
        ...mapActions({
            getRunningInvoice: "site/getRunningInvoice",
        }),
        isFreePlan(item) {
            const price = _.get(item, "details.selectedFeature.price", 0);
            return price === 0 || item.isFreePlan;
        },
        getSelectedCount(item) {
            return _.get(item, "details.selectedFeature.count", 0);
        },
        getPackageDetails(item) {
            const details = [];
            const planType = item.planType;

            if (planType === "storage") {
                details.push(`(${(item?.details?.exceededSpace || 0).toFixed(2)}GB)`);
            } else if (planType === "automation") {
                details.push(this.isFreePlan(item) ? "(Free Plan)" : `(${this.getSelectedCount(item)} Tasks)`);
            } else if (planType === "supsis") {
                details.push(`(${item.details.agentCount} Agent)`);
                if (item.details.freeAgentCount) {
                    details.push(`(+${item.details.freeAgentCount} Ücretsiz)`);
                }
            } else if (CHANNEL_PLAN_TYPES.includes(planType)) {
                details.push(`(${item.details.channelCount} Kanal)`);
            } else if (planType === "chatbot") {
                details.push(`(${item.details.totalChatbotSessionCount} Chatbot)`);
            } else if (planType === "mail") {
                details.push(this.isFreePlan(item) ? "(Free Plan)" : `(${this.getSelectedCount(item)} Mail)`);
            } else if (planType === "web" && item.details.trafficCount) {
                details.push(`(${item.details.trafficCount} Web Traffic)`);
            }

            return details.join(" ");
        },
        async getInvoice() {
            const invoiceId = this.$route.params.id;
            const vatRate = (this.site?.vatRate || 20) / 100;
            if (invoiceId === "forecast") {
                const runningInvoiceDetails = await this.getRunningInvoice();
                const utilToday = getDetailInvoice({
                    price: runningInvoiceDetails.untilToday?.totalPrice || 0,
                    vatRate: vatRate,
                    createdDate: moment().startOf("month").format("YYYY-MM-DD"),
                    type: "monthly",
                    paid: false,
                    details: runningInvoiceDetails.untilToday?.details || {},
                });

                const endOfMonthForecast = getDetailInvoice({
                    price: runningInvoiceDetails.endOfMonthForecast?.totalPrice || 0,
                    vatRate: vatRate,
                    createdDate: moment().startOf("month").format("YYYY-MM-DD"),
                    type: "monthly",
                    paid: false,
                    details: runningInvoiceDetails.endOfMonthForecast?.details || {},
                });

                this.untilToday = utilToday;
                this.endOfMonthForecast = endOfMonthForecast;
            } else {
                getInvoiceById(invoiceId).then((data) => {
                    const detail = getDetailInvoice(data.invoice);
                    this.invoiceDetails = {
                        isManuel: data.invoice.isManuel || data.invoice.type === "manuel",
                        description: data.invoice.description,
                        refund: data.invoice.refund,
                        vatRate: data.invoice.vatRate !== undefined ? data.invoice.vatRate * 100 : null, // Convert to percentage
                        type: data.invoice.type,
                        ...detail,
                    };
                });
            }
        },
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
        isForecast() {
            return this.$route.params.id === "forecast";
        },
        showingInvoice() {
            return this.isForecast
                ? this.showingUntilToday
                    ? this.untilToday
                    : this.endOfMonthForecast
                : this.invoiceDetails;
        },
        vatRate() {
            if (this.showingInvoice?.vatRate !== null && this.showingInvoice?.vatRate !== undefined) {
                return this.showingInvoice.vatRate;
            }
            if (this.isForecast) {
                return this.site?.vatRate || 20;
            }
            return this.site?.vatRate || 20;
        },
        breadcrumbPaths() {
            return [
                { title: this.$t("billing.invoiceManagement"), path: "/settings/billing" },
                { title: this.isForecast ? "Tahmini Fatura" : "Fatura Detay" },
            ];
        },
    },
    mounted() {
        this.getInvoice();
    },
};
</script>

<style lang="scss" scoped>
#invoiceDetail {
    .breadcrumb {
        margin-bottom: 24px;
    }

    .forecast-toggle {
        width: 125px !important;

        &::v-deep .ant-switch-inner {
            font-size: 12px;
            padding: 0 8px;
        }
    }

    .invoice-detail-content {
        margin: 0 24px;
        padding: 24px;
        border-radius: 12px;
    }

    .invoice-summary {
        display: flex;
        flex-wrap: wrap;
        gap: 32px;
        margin-bottom: 32px;

        .summary-card {
            background: #f8fafc;
            border-radius: 8px;
            padding: 24px;
            min-width: 300px;
            width: 100%;

            .summary-item {
                margin-bottom: 16px;

                label {
                    display: block;
                    color: #64748b;
                    font-size: 13px;
                    margin-bottom: 4px;
                }

                .value {
                    color: #1e293b;
                    font-weight: 500;

                    &.description {
                        white-space: pre-wrap;
                        line-height: 1.5;
                    }
                }
            }
        }

        .price-summary {
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            min-width: 300px;
            width: 100%;

            &__head {
                padding: 16px;
                border-bottom: 1px solid #e2e8f0;
                background: #f8fafc;
                display: flex;
                align-items: center;
                justify-content: space-between;

                .summary-head__period {
                    display: flex;
                    align-items: center;
                    font-size: 16px;
                }

                .summary-head__status {
                    .status {
                        font-weight: 600;
                        padding: 4px 8px;
                        border-radius: 4px;
                        display: inline-block;

                        &.paid {
                            background: #f0fdf4;
                            color: #16a34a;
                        }

                        &.not-paid {
                            background: #fff5f5;
                            color: #e53e3e;
                        }

                        &.forecast {
                            background: #faeec5;
                            color: #f1c40f;
                        }

                        &.refund {
                            background: #fff5f5;
                            color: #e53e3e;
                        }
                    }
                }
            }

            &__content {
                padding: 24px;
            }

            .price-item {
                display: flex;
                justify-content: space-between;
                margin-bottom: 12px;

                &.tax {
                    color: #475569;
                    font-size: 14px;
                }

                &.total {
                    font-weight: 600;
                    color: #1e293b;
                    padding-top: 16px;
                    border-top: 1px solid #e2e8f0;
                    margin-top: 16px;
                }
            }
        }
    }

    .package-groups {
        width: 100%;
        display: flex;
        flex-direction: column;
        background: #fff;
        border: 1px solid #f0f0f0;
        border-radius: 8px;
        overflow: hidden;

        .package-group:first-child .group-header {
            border-top: 0;
        }

        .package-group {
            width: 100%;
            display: flex;
            flex-direction: column;

            .group-header {
                padding: 12px 16px;
                background: #f8f9fc;
                border-bottom: 1px solid #e8e8e8;
                border-top: 1px solid #e8e8e8;
                display: flex;
                justify-content: space-between;

                h3 {
                    font-size: 15px;
                    margin: 0;
                    color: #475569;
                }

                .group-total {
                    font-size: 14px;
                    font-weight: bold;
                }
            }

            .package-items {
                display: flex;
                flex-direction: column;

                .package-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 12px;
                    border-radius: 4px;
                    background: #fff;

                    &:nth-child(odd) {
                        background: #fafafa;
                    }

                    &:hover {
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                        transform: none;
                    }

                    .item-info {
                        .item-title {
                            font-size: 14px;
                            font-weight: 600;

                            .item-details {
                                font-size: 13px;
                                font-weight: 400;
                            }
                        }

                        .item-period {
                            font-size: 10px;
                        }
                    }

                    .item-price {
                        font-size: 14px;
                        font-weight: bold;
                    }
                }
            }
        }
    }
}
</style>
